/**
 * Reviews side panel — Canva-style.
 * Mounted in VideoWorkspacePanels next to the existing Audio panel.
 *
 * - Top: filter pills (All / Open / Sent)
 * - Body: scrollable list of ReviewCards (newest first)
 * - Empty state when no reviews exist
 *
 * Actions:
 *   - "Add to chat" enqueues into reviewQueueStore + sets status=accepted server-side.
 *   - "Dismiss" sets status=dismissed.
 *   - Click anchor chip → calls onSeek (seeks the player + switches to that slide).
 */

import { useMemo, useState } from 'react';
import { Inbox, MessageSquare, RefreshCw, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { studioBOn } from '../../../lib/studioB/flag';
import { PanelHeaderB } from '../PanelHeaderB';
import { BarIconB } from '../BarIconB';
import { useReviews } from './useReviews';
import { useReviewQueueStore } from '../../../stores/reviewQueueStore';
import { ReviewCard } from './ReviewCard';
import type { OwnerReview, ReviewCommentStatus } from './types';

type Filter = 'open' | 'queued' | 'sent';

interface Props {
  conversationId: string;
  onClose: () => void;
  /** Hooks the workspace player to a (timestamp, slideId) seek. */
  onSeekTo: (timestamp: number, slideId: string) => void;
  /** Fill its container (Direction B's phone sheet) instead of the side panel's fixed width. */
  fill?: boolean;
}

export function ReviewsPanel({ conversationId, onClose, onSeekTo, fill = false }: Props) {
  const { reviews, loading, error, refresh, updateCommentStatus, removeReview } = useReviews(conversationId, true);
  const enqueue = useReviewQueueStore((s) => s.enqueue);
  const removeFromQueue = useReviewQueueStore((s) => s.remove);
  const queuedIds = useReviewQueueStore((s) => {
    const list = s.queuesByConversation[conversationId];
    return new Set(list?.map((q) => q.commentId) ?? []);
  });

  // Default to 'open' — the panel's primary job is "what still needs my decision."
  // Accepted comments live in the chip stack above the composer; sent comments
  // are archived; both are accessible via filter pills but never the default view.
  const [filter, setFilter] = useState<Filter>('open');

  const filtered = useMemo(() => filterReviews(reviews, filter), [reviews, filter]);

  const counts = useMemo(() => {
    const c = { open: 0, queued: 0, sent: 0 };
    for (const r of reviews) {
      for (const cm of r.comments) {
        if (cm.status === 'pending') c.open++;
        else if (cm.status === 'accepted') c.queued++;
        else if (cm.status === 'sent') c.sent++;
      }
    }
    return c;
  }, [reviews]);

  const handleAddToChat = async (review: OwnerReview, commentId: string) => {
    const comment = review.comments.find((c) => c.id === commentId);
    if (!comment) return;
    enqueue(conversationId, {
      reviewId: review.id,
      reviewerName: review.reviewerName,
      commentId: comment.id,
      text: comment.text,
      anchor: {
        kind: 'slide',
        slideId: comment.slideId,
        slideIndex: comment.slideIndex,
        slideTitle: comment.slideTitle,
        timestamp: comment.timestamp,
      },
    });
    await updateCommentStatus(review.id, commentId, { status: 'accepted' });
  };

  const handleRemoveFromChat = (commentId: string) => {
    removeFromQueue(conversationId, commentId);
    // Find the review that owns this comment so we can revert its status.
    const owningReview = reviews.find((r) => r.comments.some((c) => c.id === commentId));
    if (owningReview) {
      updateCommentStatus(owningReview.id, commentId, { status: 'pending' });
    }
  };

  const handleSetStatus = (review: OwnerReview, commentId: string, status: ReviewCommentStatus) => {
    if (status !== 'accepted' && queuedIds.has(commentId)) {
      removeFromQueue(conversationId, commentId);
    }
    updateCommentStatus(review.id, commentId, { status });
  };

  return (
    <div className={cn(fill ? 'w-full' : studioBOn() ? 'w-[380px]' : 'w-[340px]', 'h-full flex flex-col')}>
      {/* Header */}
      {studioBOn() ? (
        <PanelHeaderB title="Reviews" count={reviews.length} onClose={onClose}>
          <BarIconB label="Refresh reviews" onClick={refresh} disabled={loading} className={cn(loading && 'animate-spin')}>
            <RefreshCw className="h-4 w-4" />
          </BarIconB>
        </PanelHeaderB>
      ) : (
      <div className="h-10 flex items-center justify-between px-3 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-lg bg-accent flex items-center justify-center">
            <MessageSquare className="w-3.5 h-3.5 text-accent-foreground" />
          </div>
          <span className="text-xs font-semibold studio-text-primary truncate">Reviews</span>
          {reviews.length > 0 && (
            <span className="text-[10px] studio-text-secondary">({reviews.length})</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={refresh}
            title="Refresh"
            className={cn(
              'w-6 h-6 rounded-md flex items-center justify-center studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors',
              loading && 'animate-spin',
            )}
            disabled={loading}
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-md flex items-center justify-center studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      )}

      {/* Filter pills */}
      {reviews.length > 0 && (
        <div className="px-3 pb-2 flex items-center gap-1.5 flex-shrink-0">
          <FilterPill active={filter === 'open'} onClick={() => setFilter('open')} label="Open" count={counts.open} accent="amber" />
          <FilterPill active={filter === 'queued'} onClick={() => setFilter('queued')} label="Queued" count={counts.queued} accent="blue" />
          <FilterPill active={filter === 'sent'} onClick={() => setFilter('sent')} label="Sent" count={counts.sent} accent="emerald" />
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="m-3 p-3 rounded-md text-xs bg-destructive/10 text-destructive">
            {error}
          </div>
        )}
        {!loading && reviews.length === 0 && !error && <EmptyState />}
        {reviews.length > 0 && filtered.length === 0 && (
          <FilteredEmptyState filter={filter} counts={counts} />
        )}
        <div className="px-3 pb-4 space-y-2">
          {filtered.map((r) => (
            <ReviewCard
              key={r.id}
              review={r}
              queuedCommentIds={queuedIds}
              onSeek={onSeekTo}
              onAddToChat={handleAddToChat}
              onRemoveFromChat={handleRemoveFromChat}
              onSetCommentStatus={handleSetStatus}
              onDeleteReview={removeReview}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  label,
  count,
  accent,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  accent?: 'amber' | 'emerald' | 'blue';
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors',
        active
          ? 'bg-black/[0.08] dark:bg-white/[0.12] studio-text-primary'
          : 'studio-text-secondary hover:bg-black/[0.04] dark:hover:bg-white/[0.06]',
      )}
    >
      {label}
      <span
        className={cn(
          'inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] tabular-nums',
          accent === 'amber' && 'bg-warning/20 text-foreground',
          accent === 'blue' && 'bg-accent text-accent-foreground',
          accent === 'emerald' && 'bg-success/15 text-success',
          !accent && 'bg-black/[0.08] dark:bg-white/[0.10]',
        )}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * Empty state when the filter has nothing — written so the creator always knows
 * what state they're in across the three pipelines (Open / Queued / Sent).
 */
function FilteredEmptyState({
  filter,
  counts,
}: {
  filter: Filter;
  counts: { open: number; queued: number; sent: number };
}) {
  if (filter === 'open') {
    if (counts.queued === 0 && counts.sent > 0) {
      return (
        <div className="px-6 py-10 text-center">
          <div className="text-2xl mb-2">✓</div>
          <p className="text-[13px] font-medium studio-text-primary">All caught up</p>
          <p className="text-[11px] studio-text-secondary mt-1">
            {counts.sent} comment{counts.sent === 1 ? '' : 's'} sent to Ivy.
          </p>
        </div>
      );
    }
    if (counts.queued > 0) {
      return (
        <div className="px-6 py-10 text-center">
          <div className="text-2xl mb-2">📤</div>
          <p className="text-[13px] font-medium studio-text-primary">All triaged</p>
          <p className="text-[11px] studio-text-secondary mt-1">
            {counts.queued} comment{counts.queued === 1 ? '' : 's'} queued — send {counts.queued === 1 ? 'it' : 'them'} to Ivy when ready.
          </p>
        </div>
      );
    }
    return (
      <div className="px-6 py-10 text-center">
        <p className="text-[12px] studio-text-secondary">No comments waiting on you.</p>
      </div>
    );
  }
  if (filter === 'queued') {
    return (
      <div className="px-6 py-10 text-center">
        <p className="text-[12px] studio-text-secondary">
          Nothing queued. Add comments to chat from <span className="font-medium">Open</span> to stage them for Ivy.
        </p>
      </div>
    );
  }
  return (
    <div className="px-6 py-10 text-center">
      <p className="text-[12px] studio-text-secondary">No comments have been sent to Ivy yet.</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-6 py-12 text-center">
      <div className="mx-auto w-10 h-10 rounded-full bg-black/[0.04] dark:bg-white/[0.05] flex items-center justify-center mb-3">
        <Inbox className="w-5 h-5 studio-text-secondary" />
      </div>
      <p className="text-[13px] font-medium studio-text-primary">No comments yet</p>
      <p className="text-[11px] studio-text-secondary mt-1 leading-relaxed">
        Turn on comments for a share link in Publish, and send people its review link. They can leave a note at any moment of the video, and it shows up here.
      </p>
    </div>
  );
}

function filterReviews(reviews: OwnerReview[], filter: Filter): OwnerReview[] {
  return reviews
    .map((r) => ({
      ...r,
      comments: r.comments.filter((c) => {
        if (filter === 'open') return c.status === 'pending';
        if (filter === 'queued') return c.status === 'accepted';
        return c.status === 'sent';
      }),
    }))
    .filter((r) => r.comments.length > 0);
}
